import React, { useState, useContext } from 'react'
import { useAccordionToggle, Accordion, Card, Modal, AccordionContext } from 'react-bootstrap'
import getConfig from 'next/config'
import styles from './EmailOptInModal.module.scss'
import { useTranslation } from 'next-i18next'

const { publicRuntimeConfig } = getConfig()

function ModalToggle({ children, eventKey, callback }) {
  const [open, setOpen] = useState(false)
  const currentEventKey = useContext(AccordionContext)

  const decoratedOnClick = useAccordionToggle(eventKey, () => callback && callback(eventKey))

  const isCurrentEventKey = currentEventKey === eventKey

  if (isCurrentEventKey && !open) {
    setOpen(true)
  } else if (!isCurrentEventKey && open) {
    setOpen(false)
  }

  return (
    <button type="button" onClick={decoratedOnClick}>
      {children}
      <img
        src="/images/chevron-s.svg"
        alt="[Icon] Interaction"
        width={24}
        height={24}
        className={styles.modalToggleArrow}
        style={{
          transform: open ? 'rotate(-180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease-in-out'
        }}
      />
    </button>
  )
}

const EmailOptInModal = ({ show, setShow, linkId, ppId, linkText = '', country = null }) => {
  const isLocationKorea = country === 'KR'
  const { t } = useTranslation('common')
  const handleClose = () => setShow(false)
  const handleShow = (event) => {
    event.preventDefault()
    setShow(true)
  }

  const defaultModalContent = {
    title: t('Details'),
    body: (
      <ul className={styles.detailsList}>
        <li>{t('Email_opt_in_message_description')}</li>
        <li>{t('You_can_turn_this_off_text')}</li>
        <li>
          {t('For_more_information_text')}&nbsp;
          <a
            href={`${publicRuntimeConfig.kp4BaseUrl}/web-privacy-policy`}
            id={ppId}
            target="_blank"
            rel="noreferrer"
          >
            {t('Samsung_Knox_Privacy_Policy_text')}
          </a>
          .
        </li>
      </ul>
    )
  }

  const modalContent = {
    title: isLocationKorea
      ? '삼성 Knox 마케팅∙광고 목적 개인정보 수집 및 이용 동의'
      : defaultModalContent.title,
    body: isLocationKorea ? (
      <div className={styles.modalBody}>
        <p>
          귀하께서는 삼성 Knox 개인정보 수집∙이용에 대한 동의를 거부하실 수 있으며, 이는
          삼성Knox서비스 이용에 필수적으로 제공되어야 하는 정보가 아니므로, 동의를 거부하시더라도
          마케팅∙광고 정보 제공 이외의 삼성 Knox계정, 및 삼성 Knox계정 기반 서비스 이용은
          가능합니다. 다만 삼성 Knox 마케팅·광고 제공을 위한 개인정보 수집·이용에 대하여 동의하지
          않으실 경우, 삼성 Knox 마케팅∙광고 제공 등에 제한이 있을 수 있습니다.
        </p>
        <p>
          개인정보 처리에 대한 상세한 사항은 삼성 Knox 홈페이지(
          <a href={`${publicRuntimeConfig.kp4BaseUrl}`}>www.samsungknox.com</a>)에 공개한
          '개인정보처리방침'을 참조하십시오.
        </p>
        <Accordion>
          <Card>
            <ModalToggle eventKey="0">수집하는 항목</ModalToggle>
            <Accordion.Collapse eventKey="0">
              <Card.Body>
                <ul>
                  <li>
                    삼성계정, 삼성계정 기반 서비스 가입/이용 과정에서 수집된 정보
                    <ul>
                      <li>
                        ID (이메일 주소/휴대폰 번호), 비밀번호, 성명, 생년월일, 국가정보, 휴대폰
                        번호, 주소
                      </li>
                      <li>
                        기기 정보 (IMEI, 단말모델정보, MCC, MNC, 단말OS정보, Mac Address,
                        접속IP정보, 최초 개통일, 제조사, 통신사 정보 등)
                      </li>
                      <li>삼성계정 서비스 이용 기록</li>
                    </ul>
                  </li>
                  <li>제품 사용정보 및 상태 모니터링 정보, 제품 진단정보</li>
                  <ul>
                    <li>
                      마케팅 활동ㆍ이벤트 관련 정보 (마케팅활동 내용 및 결과, 이벤트참여 내용 및
                      결과)
                    </li>
                    <li>관심 제품 고객 조사 결과</li>
                  </ul>
                  <li>
                    마케팅∙이벤트∙광고 콘텐츠 관련 정보 (마케팅∙이벤트∙광고 반응 정보, 쿠폰코드,
                    경품선택, 수령결과 등)
                  </li>
                  <li>삼성계정으로 로그인 가능한 삼성 서비스 이용시, 해당 서비스 이용 기록</li>
                  <li>단말이용기록 (앱 설치 기록, 앱 실행 기록)</li>
                  <li>광고ID</li>
                </ul>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <ModalToggle eventKey="1">수집∙이용 목적</ModalToggle>
            <Accordion.Collapse eventKey="1">
              <Card.Body>
                <p>마케팅∙광고 목적으로 활용되며, 주요 활용 목적은 다음과 같습니다.</p>
                <ul>
                  <li>제품 및 서비스 소식 전달, 구매 또는 이용권유</li>
                  <li>이벤트 정보 및 혜택 제공</li>
                  <li>서비스 이용시 광고 제공</li>
                  <li>개인화/맞춤형 마케팅/광고</li>
                  <li>제공 마케팅/광고 성과 측정</li>
                </ul>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
          <Card>
            <ModalToggle eventKey="2">보유∙이용 기간</ModalToggle>
            <Accordion.Collapse eventKey="2">
              <Card.Body>회원 탈퇴 또는 별도 삭제 요청이 있을 시까지</Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      </div>
    ) : (
      defaultModalContent.body
    )
  }

  return (
    <>
      <a href="javascript:void(0)" role={'button'} onClick={handleShow} id={linkId}>
        {linkText}
      </a>
      <Modal show={show} onHide={handleClose} className={styles.modalContainer}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <div className={styles.modalHeaderWrapper}>
            <h3>{modalContent.title}</h3>
            {isLocationKorea && (
              <span>
                삼성전자는 마케팅 및 광고 목적으로 최초 회원 가입 또는 서비스 이용 시 이용자로부터
                아래와 같은
                <br /> 개인정보를 수집하고 있습니다.
              </span>
            )}
          </div>
        </Modal.Header>
        <Modal.Body className={styles.modalBodyContainer}>{modalContent.body}</Modal.Body>
      </Modal>
    </>
  )
}

export default EmailOptInModal
